import { z } from 'zod'
import { creditsSchema, positiveCreditsSchema, stakeSchema } from './credits.ts'

/**
 * Contract v2 between the cabinet and its game server, whatever carries it
 * (the backend's cabinet `game.*` procedures; the engine's local server in
 * unit tests). The server owns the wallet, the round, the feature session
 * and the gamble ladder; the cabinet reads the game state and sends what
 * the player did as a play action, and shows what comes back. Every
 * response says what the server accepts next (`allowed`); a play response
 * says how it moved the wallet.
 *
 * Two calls: `state` (boot, recovery) and `play`. The envelope of both is
 * game-agnostic; what belongs to a kind of game sits under `game`, told
 * apart by `kind` (`'lineSlot'` today), and a play reply's `game` by the
 * action it answers.
 *
 * These schemas check the SHAPE of a message (field types, the credits
 * brand). Responses reach the cabinet as `unknown` (`GameServer`) and become
 * these types through the cabinet's hand-written decoders, which also check
 * every rule a single response must keep (ledger, totals, escrow, …).
 * Keyed collections are arrays: a numeric object key comes back from JSON
 * as a string.
 */

export const CONTRACT_VERSION = 2 as const
export type ContractVersion = typeof CONTRACT_VERSION

const contractVersionSchema = z.literal(CONTRACT_VERSION)
const nonEmptyString = z.string().min(1)
const nonNegativeInt = z.int().min(0)
const positiveInt = z.int().min(1)

// -----------------------------------------------------------------------------
// Scalars
// -----------------------------------------------------------------------------

/** The colour the player picks. */
export const gambleChoiceSchema = z.enum(['red', 'black'])
export type GambleChoice = z.infer<typeof gambleChoiceSchema>

const GAMBLE_SYMBOL_VALUES = ['spades', 'clubs', 'diamonds', 'hearts'] as const

/** A card the server reveals. */
export const gambleSymbolSchema = z.enum(GAMBLE_SYMBOL_VALUES)
export type GambleSymbol = z.infer<typeof gambleSymbolSchema>

/** Every card, in the order a drawn value in `[0, 4)` indexes. */
export const GAMBLE_SYMBOLS: readonly GambleSymbol[] = GAMBLE_SYMBOL_VALUES

const SYMBOL_TO_COLOUR: Record<GambleSymbol, GambleChoice> = {
  spades: 'black',
  clubs: 'black',
  diamonds: 'red',
  hearts: 'red',
}

/** The colour of a card: a pick wins when it names it. */
export function gambleSymbolToColour(symbol: GambleSymbol): GambleChoice {
  return SYMBOL_TO_COLOUR[symbol]
}

/** A paid spin costs the stake; a free spin is a game of a feature. */
export const spinKindSchema = z.enum(['paid', 'free'])
export type SpinKind = z.infer<typeof spinKindSchema>

/** What a play request asks for: the `type` of its action. */
export const playActionTypeSchema = z.enum([
  'spin',
  'collect',
  'gamble',
  'pick',
])
export type PlayActionType = z.infer<typeof playActionTypeSchema>

/**
 * What the server accepts next for this session: the play actions it takes.
 * No open round: `['spin']`.
 */
export const nextActionSchema = playActionTypeSchema
export type NextAction = PlayActionType

/** The actions that settle a round's pending win. */
export type SettleAction = Extract<PlayActionType, 'collect' | 'gamble'>

export const pendingWinSourceSchema = z.enum(['spin', 'feature', 'gamble'])
export type PendingWinSource = z.infer<typeof pendingWinSourceSchema>

/** Money won in the open round, not yet in the wallet. Always above 0. */
export const pendingWinSchema = z.object({
  source: pendingWinSourceSchema,
  amount: positiveCreditsSchema,
})
export type PendingWin = z.infer<typeof pendingWinSchema>

// -----------------------------------------------------------------------------
// Provably fair
// -----------------------------------------------------------------------------

/**
 * The independent provably fair sequences of a session, each with its own
 * seeds and nonce: drawing a gamble card never moves the spin nonce.
 */
export const rngStreamSchema = z.enum(['spin', 'gamble'])
export type RngStream = z.infer<typeof rngStreamSchema>

/**
 * Proof data for provably fair verification: with the revealed server seed,
 * it reproduces one draw (`verifySpin`).
 */
export const spinProofSchema = z.object({
  /** The sequence the draw came from. */
  stream: rngStreamSchema,

  /** The nonce used for this spin */
  nonce: nonNegativeInt,

  /** Hash of the server seed (commitment — shown before spin) */
  serverSeedHash: z.string(),

  /** Client seed used */
  clientSeed: z.string(),

  /**
   * The server seed itself — only revealed after seed rotation.
   * Will be null during active play.
   */
  serverSeed: z.string().nullable(),

  /**
   * The range of each drawn value, in draw order: value i is in
   * `[0, ranges[i])`. A paid spin's are its reel strip lengths, then the
   * feature's extra ranges; a free game's, its reel strip lengths.
   */
  ranges: z.array(positiveInt),
})
export type SpinProof = z.infer<typeof spinProofSchema>

/** What a player may see of a stream before its server seed is revealed. */
export const seedCommitmentSchema = z.object({
  serverSeedHash: z.string(),
  clientSeed: z.string(),
  nextNonce: nonNegativeInt,
})
export type SeedCommitment = z.infer<typeof seedCommitmentSchema>

// -----------------------------------------------------------------------------
// Spin result
// -----------------------------------------------------------------------------

/**
 * The visible symbol grid after a spin.
 * grid[reelIndex][rowIndex] = symbol ID.
 *
 * For a 5-reel, 3-row game:
 *   grid[0] = ["cherry", "bar", "plum"]       ← reel 1, rows top to bottom
 *   grid[1] = ["seven", "cherry", "blank"]     ← reel 2
 *   ...etc
 */
export const symbolGridSchema = z.array(z.array(z.string()))
export type SymbolGrid = z.infer<typeof symbolGridSchema>

/** A payout multiplier: the config may hold any non-negative factor. */
const multiplierSchema = z.number().min(0)

/**
 * A single payline win within a spin result.
 */
export const paylineWinSchema = z.object({
  /** Which payline produced this win (0-based index into the payline definitions array) */
  paylineIndex: nonNegativeInt,

  /** The symbol that formed the winning combination */
  symbolId: z.string(),

  /** How many consecutive symbols matched (e.g. 3, 4, or 5) */
  matchCount: positiveInt,

  /** Payout multiplier from the paytable for this combination */
  payoutMultiplier: multiplierSchema,

  /**
   * Actual win amount (payoutMultiplier × bet per line). In a free game of
   * a `freeSpinsMultiplier` feature it is times the feature's
   * `winMultiplier` (the amount the cabinet shows, so it multiplies
   * nothing itself) while `payoutMultiplier` stays the pay table's. In a game with a
   * wild rule (`GameConfig.wildRule`), `payoutMultiplier` is what the line
   * pays: the paytable's multiplier times the rule's substitution
   * multiplier when a wild completes the symbol, and `symbolId` is the
   * symbol that pays (the wild for a win of the wild's own run), with
   * `matchCount` the count it pays.
   */
  winAmount: nonNegativeInt,
})
export type PaylineWin = z.infer<typeof paylineWinSchema>

/**
 * A scatter win (not tied to a payline).
 */
export const scatterWinSchema = z.object({
  /** The scatter symbol ID */
  symbolId: z.string(),

  /** How many scatters appeared on the grid */
  count: positiveInt,

  /** Payout multiplier from the paytable */
  payoutMultiplier: multiplierSchema,

  /**
   * Actual win amount (payoutMultiplier × total bet), times the feature's
   * `winMultiplier` in a free game of a `freeSpinsMultiplier` feature.
   */
  winAmount: nonNegativeInt,
})
export type ScatterWin = z.infer<typeof scatterWinSchema>

/**
 * Free-spins expanding-symbol win. Paid after regular wins: the symbol fills
 * every reel it appears on and pays on all played lines, even on
 * non-adjacent reels.
 */
export const expandingWinSchema = z.object({
  symbolId: z.string(),

  /** 0-based indices of the reels the symbol expands on */
  reels: z.array(nonNegativeInt),

  /** Number of reels showing the symbol (= reels.length) */
  matchCount: positiveInt,

  /** Payout multiplier from the paytable for matchCount */
  payoutMultiplier: multiplierSchema,

  /** payoutMultiplier × betPerLine × played lines; the sum of `lineWins` */
  winAmount: nonNegativeInt,

  /**
   * Per-line breakdown, one entry per played line — every line pays the
   * same amount (payoutMultiplier × betPerLine). `paylineIndex` is 0-based
   * into the config's paylines, covering the played lines `0..lines-1`
   * (lines beyond the config's paylines are not played).
   * Lets the UI present each played line individually (Book of Ra rule: one
   * at a time, own sound) without doing pay math itself.
   */
  lineWins: z.array(
    z.object({ paylineIndex: nonNegativeInt, winAmount: nonNegativeInt }),
  ),
})
export type ExpandingWin = z.infer<typeof expandingWinSchema>

/**
 * Complete result of a single spin, with the proof of its draw: the
 * engine's `SpinState`. Its amounts are plain non-negative integers, not
 * Credits: they are the engine's arithmetic, the wallet moves by the
 * envelope's credits.
 */
export const spinResultSchema = z.object({
  /** The visible symbol grid */
  grid: symbolGridSchema,

  /** All payline wins */
  paylineWins: z.array(paylineWinSchema),

  /** All scatter wins */
  scatterWins: z.array(scatterWinSchema),

  /** Expanding-symbol win (free spins only), or null */
  expandingWin: expandingWinSchema.nullable(),

  /** Total win amount (payline + scatter + expanding wins) */
  totalWin: nonNegativeInt,

  /** The stop positions on each reel (for verification) */
  stopPositions: z.array(nonNegativeInt),

  /** Provably fair proof data */
  proof: spinProofSchema,
})
export type SpinResult = z.infer<typeof spinResultSchema>

// -----------------------------------------------------------------------------
// Feature
// -----------------------------------------------------------------------------

/**
 * A feature's progress, game-agnostic. `data` belongs to the feature kind
 * (`featureDataDecoders[featureKind]`), e.g. Book of Ra's expanding symbol
 * and its pays.
 */
export const featureStateSchema = z.object({
  gamesPlayed: nonNegativeInt,
  /** Awarded so far, retriggers included. Games left: gamesTotal − gamesPlayed. */
  gamesTotal: nonNegativeInt,
  /** Sum of the free games' wins; the trigger win is not part of it. */
  featureWin: creditsSchema,
  data: z.unknown(),
})
export type FeatureState = z.infer<typeof featureStateSchema>

const featureUpdateBase = {
  featureId: nonEmptyString,
  /** A registered feature kind (`featureDataDecoders`). */
  featureKind: nonEmptyString,
  state: featureStateSchema,
}

/** What a spin did to the feature. */
export const featureUpdateSchema = z.discriminatedUnion('kind', [
  /** A paid spin opened it. */
  z.object({
    kind: z.literal('triggered'),
    ...featureUpdateBase,
    gamesAwarded: positiveInt,
  }),
  /** A free game added games. */
  z.object({
    kind: z.literal('retriggered'),
    ...featureUpdateBase,
    gamesAwarded: positiveInt,
  }),
  /** A free game with games left after it. */
  z.object({ kind: z.literal('continued'), ...featureUpdateBase }),
  /** The last free game: gamesPlayed === gamesTotal. */
  z.object({ kind: z.literal('completed'), ...featureUpdateBase }),
])
export type FeatureUpdate = z.infer<typeof featureUpdateSchema>

// -----------------------------------------------------------------------------
// Gamble
// -----------------------------------------------------------------------------

/** One pick, evaluated by the server. */
export const gamblePickSchema = z.object({
  choice: gambleChoiceSchema,
  card: gambleSymbolSchema,
  won: z.boolean(),
  /** One value of range 4 on the gamble stream. */
  proof: spinProofSchema,
})
export type GamblePick = z.infer<typeof gamblePickSchema>

export const gambleLadderSchema = z.object({
  /** The amount at risk. */
  total: positiveCreditsSchema,
  /** Picks made on this ladder. */
  attempt: nonNegativeInt,
  /** For display: whether another pick is allowed comes from `allowed`. */
  maxAttempts: nonNegativeInt,
  /** Most recent first, at most `maxAttempts` cards. */
  history: z.array(gambleSymbolSchema),
  lastPick: gamblePickSchema.nullable(),
})
export type GambleLadder = z.infer<typeof gambleLadderSchema>

// -----------------------------------------------------------------------------
// Open round
// -----------------------------------------------------------------------------

/**
 * A game round: a paid spin, the feature it triggered and the gamble of its
 * win. Open until its win is settled, or until it is over with nothing won.
 */
export const openRoundSchema = z.object({
  roundId: nonEmptyString,
  /** Locked at the paid spin; every free game is played at it. */
  stake: stakeSchema,
  lastSpin: z.object({
    spinKind: spinKindSchema,
    result: spinResultSchema,
    winCredited: creditsSchema,
  }),
  pendingWin: pendingWinSchema.nullable(),
  /** The last update sent for this round. */
  feature: featureUpdateSchema.nullable(),
  gamble: gambleLadderSchema.nullable(),
})
export type OpenRound = z.infer<typeof openRoundSchema>

// -----------------------------------------------------------------------------
// Game state (boot, recovery)
// -----------------------------------------------------------------------------

export const gameStateRequestSchema = z.object({
  version: contractVersionSchema,
  type: z.literal('gameState'),
  /** A fresh id per request: a reply echoes it. */
  requestId: nonEmptyString,
  /**
   * The game's slug (`book-of-ra-2`): a game server plays one game and
   * refuses another's (`INVALID_REQUEST`).
   */
  gameId: nonEmptyString,
  /** null on a cold boot: the server returns (or creates) the stored session. */
  sessionId: nonEmptyString.nullable(),
})
export type GameStateRequest = z.infer<typeof gameStateRequestSchema>

/** A line slot's part of the game state. */
export const lineSlotGameStateSchema = z.object({
  kind: z.literal('lineSlot'),
  gambleHistory: z.array(gambleSymbolSchema),
  /** The session's last spin, open round or not: the reels to show after a boot. */
  lastResult: spinResultSchema.nullable(),
  openRound: openRoundSchema.nullable(),
  fairness: z.object({
    spin: seedCommitmentSchema,
    gamble: seedCommitmentSchema,
  }),
})
export type LineSlotGameState = z.infer<typeof lineSlotGameStateSchema>

export const gameStateResponseSchema = z.object({
  version: contractVersionSchema,
  type: z.literal('gameState'),
  /** The request's id, echoed. */
  requestId: nonEmptyString,
  /**
   * Counts the session's committed play calls: +1 for each. Reading the
   * game state does not count. A backend may also move it when the balance
   * changes outside play (an operator's credit-in or cash-out), so a client
   * that has not read the state since gets SEQUENCE_MISMATCH.
   */
  sequence: nonNegativeInt,
  /** The session's game: its slug, as the request named it. */
  gameId: nonEmptyString,
  sessionId: nonEmptyString,
  balance: creditsSchema,
  allowed: z.array(nextActionSchema),
  /** The game's own state, by the kind of game. */
  game: z.discriminatedUnion('kind', [lineSlotGameStateSchema]),
})
export type GameStateResponse = z.infer<typeof gameStateResponseSchema>

// -----------------------------------------------------------------------------
// Play: requests
// -----------------------------------------------------------------------------

/**
 * A spin. Paid: the player's stake. Free: the round's stake, which the
 * server ignores in favour of the one it locked.
 */
export const spinActionSchema = z.object({
  type: z.literal('spin'),
  stake: stakeSchema,
})

/** Credits the round's pending win and closes the round. */
export const collectActionSchema = z.object({
  type: z.literal('collect'),
  roundId: nonEmptyString,
})

/** Puts the round's pending win at risk on a new gamble ladder. */
export const gambleActionSchema = z.object({
  type: z.literal('gamble'),
  roundId: nonEmptyString,
})

export const pickActionSchema = z.object({
  type: z.literal('pick'),
  roundId: nonEmptyString,
  /** The ladder's attempt: a pick sent twice is refused. */
  attempt: nonNegativeInt,
  choice: gambleChoiceSchema,
})

/** What the player did in a line slot, told apart by `type`. */
export const lineSlotActionSchema = z.discriminatedUnion('type', [
  spinActionSchema,
  collectActionSchema,
  gambleActionSchema,
  pickActionSchema,
])
export type LineSlotAction = z.infer<typeof lineSlotActionSchema>

/** A play action: what the player did. */
export const playActionSchema = lineSlotActionSchema
export type PlayAction = LineSlotAction

/** The play action of `type` `Type`. */
export type ActionOf<Type extends PlayActionType> = Extract<
  PlayAction,
  { type: Type }
>

export const lineSlotPlayRequestSchema = z.object({
  version: contractVersionSchema,
  type: z.literal('play'),
  /** Idempotency key: a retry with the same id gets the stored response. */
  requestId: nonEmptyString,
  sessionId: nonEmptyString,
  /** The last `sequence` this client saw; the server refuses any other. */
  expectedSequence: nonNegativeInt,
  kind: z.literal('lineSlot'),
  action: lineSlotActionSchema,
})

/** A play call, told apart by the kind of game it plays. */
export const playRequestSchema = z.discriminatedUnion('kind', [
  lineSlotPlayRequestSchema,
])
export type PlayRequest = z.infer<typeof playRequestSchema>

// -----------------------------------------------------------------------------
// Play: responses
// -----------------------------------------------------------------------------

/** A spin's outcome: paid, or a free game of the round's feature. */
export const lineSlotSpinPlaySchema = z.object({
  kind: z.literal('lineSlot'),
  action: z.literal('spin'),
  /** The round of the paid spin; its free games carry the same id. */
  roundId: nonEmptyString,
  spinKind: spinKindSchema,
  /** The stake the spin was evaluated at. */
  stake: stakeSchema,
  result: spinResultSchema,
  feature: featureUpdateSchema.nullable(),
})

/** A collect: the pending win went to the wallet, the round is closed. */
export const lineSlotCollectPlaySchema = z.object({
  kind: z.literal('lineSlot'),
  action: z.literal('collect'),
  roundId: nonEmptyString,
  /** Always null: a collect closes any ladder (kept so every settle reply has it). */
  gamble: z.null(),
})

/** A gamble: the opened ladder. */
export const lineSlotGamblePlaySchema = z.object({
  kind: z.literal('lineSlot'),
  action: z.literal('gamble'),
  roundId: nonEmptyString,
  gamble: gambleLadderSchema,
})

/** A pick on the ladder. */
export const lineSlotPickPlaySchema = z.object({
  kind: z.literal('lineSlot'),
  action: z.literal('pick'),
  roundId: nonEmptyString,
  pick: gamblePickSchema,
  /** Null after a lost pick: the round is over. */
  gamble: gambleLadderSchema.nullable(),
})

/** A line slot's part of a play reply, told apart by the action it answers. */
export const lineSlotPlaySchema = z.discriminatedUnion('action', [
  lineSlotSpinPlaySchema,
  lineSlotCollectPlaySchema,
  lineSlotGamblePlaySchema,
  lineSlotPickPlaySchema,
])
export type LineSlotPlay = z.infer<typeof lineSlotPlaySchema>

/**
 * A play call's reply. It may move money: `balanceAfter = balanceBefore −
 * stakeCharged + winCredited`, both 0 when the action moves none.
 */
export const playResponseSchema = z.object({
  version: contractVersionSchema,
  type: z.literal('play'),
  /** The request's id, echoed. */
  requestId: nonEmptyString,
  /** The session's committed play calls, this one included. */
  sequence: nonNegativeInt,
  /** The wallet before this call. */
  balanceBefore: creditsSchema,
  /** The wallet after this call. Authoritative. */
  balanceAfter: creditsSchema,
  /** A paid spin: `stakeTotal(stake)`. Every other action: 0. */
  stakeCharged: creditsSchema,
  /**
   * A trigger's own win, credited at once (`result.totalWin`); a collect's
   * pending amount. Every other action: 0.
   */
  winCredited: creditsSchema,
  allowed: z.array(nextActionSchema),
  /**
   * A spin: `{ spin, totalWin }` or `{ feature, featureWin }` while it
   * waits. A gamble or a won pick: `{ gamble, ladder total }`. Else null.
   */
  pendingWin: pendingWinSchema.nullable(),
  /** The game's part of the reply, by the kind of game. */
  game: z.discriminatedUnion('kind', [lineSlotPlaySchema]),
})
export type PlayResponse = z.infer<typeof playResponseSchema>

/** The reply to a play action of `type` `Type`: its `game` narrowed to that action. */
export type PlayResponseOf<Type extends PlayActionType> = Omit<
  PlayResponse,
  'game'
> & { game: Extract<PlayResponse['game'], { action: Type }> }

// -----------------------------------------------------------------------------
// The cabinet's view
// -----------------------------------------------------------------------------

// What a cabinet gets of the messages above: everything it shows, nothing it
// would need to work out the odds. No stop positions, no proofs (their
// `ranges` are the reel strip lengths), no seed commitments: a cabinet, its
// network and its players cannot verify a draw (`serverSeed` is never
// revealed yet), and admin round history keeps every proof. The game server
// sends the full messages; the backend's cabinet API sends these
// (`toCabinetGameState`, `toCabinetPlayResponse`). Every schema below that
// holds draw data names what it keeps (`pick`), so a field added to the full
// message reaches no cabinet until it is added here.

/** A spin result as a cabinet sees it: the grid and its wins. */
export const cabinetSpinResultSchema = spinResultSchema.pick({
  grid: true,
  paylineWins: true,
  scatterWins: true,
  expandingWin: true,
  totalWin: true,
})
export type CabinetSpinResult = z.infer<typeof cabinetSpinResultSchema>

/** A pick as a cabinet sees it: the colour picked, the card and the outcome. */
export const cabinetGamblePickSchema = gamblePickSchema.pick({
  choice: true,
  card: true,
  won: true,
})
export type CabinetGamblePick = z.infer<typeof cabinetGamblePickSchema>

export const cabinetGambleLadderSchema = gambleLadderSchema
  .pick({ total: true, attempt: true, maxAttempts: true, history: true })
  .extend({ lastPick: cabinetGamblePickSchema.nullable() })
export type CabinetGambleLadder = z.infer<typeof cabinetGambleLadderSchema>

export const cabinetOpenRoundSchema = openRoundSchema
  .pick({ roundId: true, stake: true, pendingWin: true, feature: true })
  .extend({
    lastSpin: openRoundSchema.shape.lastSpin
      .pick({ spinKind: true, winCredited: true })
      .extend({ result: cabinetSpinResultSchema }),
    gamble: cabinetGambleLadderSchema.nullable(),
  })
export type CabinetOpenRound = z.infer<typeof cabinetOpenRoundSchema>

/** A line slot's part of the game state, without the seed commitments. */
export const cabinetLineSlotGameStateSchema = lineSlotGameStateSchema
  .pick({ kind: true, gambleHistory: true })
  .extend({
    lastResult: cabinetSpinResultSchema.nullable(),
    openRound: cabinetOpenRoundSchema.nullable(),
  })
export type CabinetLineSlotGameState = z.infer<
  typeof cabinetLineSlotGameStateSchema
>

export const cabinetGameStateResponseSchema = gameStateResponseSchema.extend({
  game: z.discriminatedUnion('kind', [cabinetLineSlotGameStateSchema]),
})
export type CabinetGameStateResponse = z.infer<
  typeof cabinetGameStateResponseSchema
>

export const cabinetLineSlotPlaySchema = z.discriminatedUnion('action', [
  lineSlotSpinPlaySchema.extend({ result: cabinetSpinResultSchema }),
  lineSlotCollectPlaySchema,
  lineSlotGamblePlaySchema.extend({ gamble: cabinetGambleLadderSchema }),
  lineSlotPickPlaySchema.extend({
    pick: cabinetGamblePickSchema,
    gamble: cabinetGambleLadderSchema.nullable(),
  }),
])
export type CabinetLineSlotPlay = z.infer<typeof cabinetLineSlotPlaySchema>

export const cabinetPlayResponseSchema = playResponseSchema.extend({
  game: z.discriminatedUnion('kind', [cabinetLineSlotPlaySchema]),
})
export type CabinetPlayResponse = z.infer<typeof cabinetPlayResponseSchema>

/** The cabinet's reply to a play action of `type` `Type`. */
export type CabinetPlayResponseOf<Type extends PlayActionType> = Omit<
  CabinetPlayResponse,
  'game'
> & { game: Extract<CabinetPlayResponse['game'], { action: Type }> }

/**
 * The game state a cabinet gets of a server's `state`: the cabinet schema
 * drops what it does not name (stop positions, proofs, seed commitments).
 * @throws ZodError when `response` is no game state.
 */
export function toCabinetGameState(
  response: GameStateResponse,
): CabinetGameStateResponse {
  return cabinetGameStateResponseSchema.parse(response)
}

/**
 * The play reply a cabinet gets of a server's `play`: the cabinet schema
 * drops what it does not name (stop positions, proofs).
 * @throws ZodError when `response` is no play reply.
 */
export function toCabinetPlayResponse<Type extends PlayActionType>(
  response: PlayResponseOf<Type>,
): CabinetPlayResponseOf<Type>
export function toCabinetPlayResponse(
  response: PlayResponse,
): CabinetPlayResponse
export function toCabinetPlayResponse(
  response: PlayResponse,
): CabinetPlayResponse {
  return cabinetPlayResponseSchema.parse(response)
}

// -----------------------------------------------------------------------------
// Transport port
// -----------------------------------------------------------------------------

/**
 * The transport port. Every method returns `unknown` on purpose: a reply is
 * typed only once a decoder has checked it.
 */
export interface GameServer {
  state(request: GameStateRequest): Promise<unknown>
  play(request: PlayRequest): Promise<unknown>
}
